/**
 * Archive API
 * Historical data archive with POD filtering
 */

import { apiClient } from "./client";
import type {
  ArchiveSummary,
  DailyArchiveItem,
  InverterChartResponse,
  MonthlyArchiveItem,
  ProductionArchiveResponse,
  ProductionChartResponse,
  RangeChartResponse,
  YearlyArchiveItem,
} from "./types";

/**
 * Get historical production data by POD/timeframe
 */
export async function getProductionArchive(params: {
  startDate?: string;
  endDate?: string;
  plantId?: number;
  limit?: number;
  offset?: number;
}): Promise<ProductionArchiveResponse> {
  const searchParams = new URLSearchParams();
  if (params.startDate) searchParams.append("start_date", params.startDate);
  if (params.endDate) searchParams.append("end_date", params.endDate);
  if (params.plantId) searchParams.append("plant_id", params.plantId.toString());
  if (params.limit) searchParams.append("limit", params.limit.toString());
  if (params.offset) searchParams.append("offset", params.offset.toString());

  const query = searchParams.toString();
  return apiClient.get<ProductionArchiveResponse>(
    `/api/v1/archive/production${query ? `?${query}` : ""}`
  );
}

/**
 * Get archive summary with production statistics
 */
export async function getArchiveSummary(params?: {
  plantId?: number;
}): Promise<ArchiveSummary> {
  const searchParams = new URLSearchParams();
  if (params?.plantId) searchParams.append("plant_id", params.plantId.toString());

  const query = searchParams.toString();
  return apiClient.get<ArchiveSummary>(
    `/api/v1/archive/summary${query ? `?${query}` : ""}`
  );
}

/**
 * Get monthly production summary for a year
 */
export async function getMonthlyArchive(params: {
  year?: number;
  plantId?: number;
}): Promise<MonthlyArchiveItem[]> {
  const searchParams = new URLSearchParams();
  if (params.year) searchParams.append("year", params.year.toString());
  if (params.plantId) searchParams.append("plant_id", params.plantId.toString());

  const query = searchParams.toString();
  return apiClient.get<MonthlyArchiveItem[]>(
    `/api/v1/archive/monthly${query ? `?${query}` : ""}`
  );
}

/**
 * Get yearly production summary
 */
export async function getYearlyArchive(params: {
  plantId?: number;
}): Promise<YearlyArchiveItem[]> {
  const searchParams = new URLSearchParams();
  if (params.plantId) searchParams.append("plant_id", params.plantId.toString());

  const query = searchParams.toString();
  return apiClient.get<YearlyArchiveItem[]>(
    `/api/v1/archive/yearly${query ? `?${query}` : ""}`
  );
}

/**
 * Get daily production summary for a date range
 */
export async function getDailyArchive(params: {
  startDate?: string;
  endDate?: string;
  plantId?: number;
}): Promise<DailyArchiveItem[]> {
  const searchParams = new URLSearchParams();
  if (params.startDate) searchParams.append("start_date", params.startDate);
  if (params.endDate) searchParams.append("end_date", params.endDate);
  if (params.plantId) searchParams.append("plant_id", params.plantId.toString());

  const query = searchParams.toString();
  return apiClient.get<DailyArchiveItem[]>(
    `/api/v1/archive/daily${query ? `?${query}` : ""}`
  );
}

/**
 * Download archive data as CSV
 */
export async function downloadArchive(params: {
  startDate?: string;
  endDate?: string;
  plantId?: number;
  deviceId?: string;
  granularity?: "15min" | "hourly" | "daily";
  includeForecast?: boolean;
}): Promise<Blob> {
  const searchParams = new URLSearchParams();
  if (params.startDate) searchParams.append("start_date", params.startDate);
  if (params.endDate) searchParams.append("end_date", params.endDate);
  if (params.plantId) searchParams.append("plant_id", params.plantId.toString());
  if (params.deviceId) searchParams.append("device_id", params.deviceId);
  if (params.granularity) searchParams.append("granularity", params.granularity);
  if (params.includeForecast) searchParams.append("include_forecast", "true");

  const query = searchParams.toString();
  const url = `/api/v1/archive/download${query ? `?${query}` : ""}`;

  // Use fetch directly for blob download
  const token = localStorage.getItem("vpp_auth_token");
  const response = await fetch(
    `${process.env.NEXT_PUBLIC_API_URL || "http://localhost:8000"}${url}`,
    {
      headers: {
        Authorization: token ? `Bearer ${token}` : "",
      },
    }
  );

  if (!response.ok) {
    throw new Error("Failed to download archive");
  }

  return response.blob();
}

/**
 * Helper to trigger CSV download in browser
 */
export async function downloadArchiveCSV(params: {
  startDate?: string;
  endDate?: string;
  plantId?: number;
  deviceId?: string;
  granularity?: "15min" | "hourly" | "daily";
  includeForecast?: boolean;
}): Promise<void> {
  const blob = await downloadArchive({ ...params, includeForecast: params.includeForecast });
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  const suffix = params.deviceId ? `_${params.deviceId}` : params.includeForecast ? "_forecast" : "";
  a.download = `vpp_archive${suffix}_${params.startDate || "all"}_${params.endDate || "all"}.csv`;
  document.body.appendChild(a);
  a.click();
  window.URL.revokeObjectURL(url);
  document.body.removeChild(a);
}

/**
 * Get production chart data for iSolarCloud-style visualization
 * Returns today, yesterday, and forecast series
 */
export async function getChartData(params: {
  date?: string;
  plantId?: number;
  granularity?: "15min" | "hourly";
}): Promise<ProductionChartResponse> {
  const searchParams = new URLSearchParams();
  if (params.date) searchParams.append("target_date", params.date);
  if (params.plantId) searchParams.append("plant_id", params.plantId.toString());
  if (params.granularity) searchParams.append("granularity", params.granularity);

  const query = searchParams.toString();
  return apiClient.get<ProductionChartResponse>(
    `/api/v1/archive/chart-data${query ? `?${query}` : ""}`
  );
}

/**
 * Get per-inverter production data for chart
 * Note: No forecast - forecast is plant-level only
 */
export async function getInverterData(params: {
  date: string;
  plantId: number;
}): Promise<InverterChartResponse> {
  const searchParams = new URLSearchParams();
  searchParams.append("target_date", params.date);
  searchParams.append("plant_id", params.plantId.toString());

  return apiClient.get<InverterChartResponse>(
    `/api/v1/archive/inverter-data?${searchParams.toString()}`
  );
}

/**
 * Get production data for a date range
 * Returns data points and available dates for calendar highlighting
 * Supports filtering by plant and/or specific inverter
 */
export async function getRangeChartData(params: {
  startDate: string;
  endDate: string;
  plantId?: number;
  deviceId?: string;
  granularity?: "15min" | "hourly";
}): Promise<RangeChartResponse> {
  const searchParams = new URLSearchParams();
  searchParams.append("start_date", params.startDate);
  searchParams.append("end_date", params.endDate);
  if (params.plantId) searchParams.append("plant_id", params.plantId.toString());
  if (params.deviceId) searchParams.append("device_id", params.deviceId);
  if (params.granularity) searchParams.append("granularity", params.granularity);

  return apiClient.get<RangeChartResponse>(
    `/api/v1/archive/range-data?${searchParams.toString()}`
  );
}
